
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";
import { get, find } from "lodash";
import { FeaturesList, UserPermissions } from "@/store/types";
import MyAccount from "../components/my-account/my-account.vue";
import NavMenuIcon from "@/components/NavMenuIcon.vue";
import { BvEvent } from "bootstrap-vue";
import api from "@/services/api.service";
import { warnLastOperator } from "@/utils/warnLastOperator";
import { ExternalMenuLink } from "@/store/external-menu-link/types";
import CallContactSetup from "./tasks/call-contact/CallContactSetup.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { RouteConfigX } from '@/router';

const CaseManagement = namespace("caseManagement");
const SiteMonitor = namespace("siteMonitor");
const ExternalMenuLinkStore = namespace("externalMenuLink");

@Component({
	components: {
		"my-account": MyAccount,
		"nav-menu-icon": NavMenuIcon,
		VuePerfectScrollbar: VuePerfectScrollbar
	}
})
export default class NavMenu extends Vue {
	@Getter getPermissions: UserPermissions;
	@Getter("getVersion") webVersion;
	@Getter("getServerName") serverName;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getUserId") userId: number;

	@Action serverLogout: any;
	@Action logout: () => void;

	@CaseManagement.Action casesLogin: () => Promise<{ url: string; token: string }>;

	@ExternalMenuLinkStore.Action loadExternalMenuLinks: () => Promise<void>;
	@ExternalMenuLinkStore.Getter("getExternalMenuLink") externalMenuLinks: ExternalMenuLink[];

	@SiteMonitor.Mutation setlastOperatorMessageVisible: (isVisibe: boolean) => void;

	private menuOpened: boolean = false;

	private imageEncodeType: Map<string, string> = new Map([
		["/", "jpg"],
		["i", "png"],
		["R", "gif"]
	]);

	public async mounted() {
		await this.loadExternalMenuLinks();
	}

	get canOpenCaseManagement() {
		return !!get(this.featuresList, ["CaseManagement"]) && !!this.getPermissions["canAccessCaseManagement"];
	}

	get routes(): any[] {
		let routes = ((this.$router as any).options.routes as RouteConfigX[])
			.filter(
				route =>
					route.meta &&
					route.meta.title &&
					route.meta.showInNav &&
					(!route.meta.show || route.meta.show(this.getPermissions, this.featuresList))
			)
			.map(route => {
				return {
					routeName: route.name,
					linkText: route.meta.title,
					show: route.meta.show,
					iconClass: route.meta.iconClass,
					iconMask: route.meta.iconMask,
					openInNewTab: route.meta.openInNewTab,
					target: this.getRouteTarget(route) // if the link should open in a new window or not
				};
			});

		// if the feature is enabled, sort the menu SortAlphabetically
		if (get(this.featuresList, ["NavMenu", "SortAlphabetically"])) {
			routes = routes.sort((a, b) => a.linkText.localeCompare(b.linkText)); // sort the list alphabetically;
		}
		return routes;
	}

	private getRouteTarget(route) {
		// Get the list of configurable OpenInNewTab routes from our feature flags, check if this route is a member of that list
		let openInNewTabRoutes = get(this.featuresList, ["NavMenu", "OpenInNewTab"], {});
		let openInNewTabRouteNames = Object.keys(openInNewTabRoutes);

		// Pull out the true/false value from the feature flag list (or default to false if this route isn't found)
		let isInNewTabRoutes =
			openInNewTabRoutes[
				openInNewTabRouteNames.find(routeName => routeName.toLowerCase() == route.name.toLowerCase())
			] || false;

		// If this route is a member of the feature flagged openInNewTab list, or the route itself has been configured to
		// always open in a new tab, then return a target of _blank. Otherwise, return _self.
		if (isInNewTabRoutes || route.meta.openInNewTab) {
			return "_blank";
		} else {
			return "_self";
		}
	}

	get externalRoutes(): any[] {
		return this.externalMenuLinks;
	}

	logoutUser(): void {
		this.$router.push({ name: "logout" });
	}

	async toCases() {
		const loginResult = await this.casesLogin();
		let url = loginResult.url + (loginResult.url.endsWith("/") ? "" : "/");
		window.open(`${url}login/withToken/${loginResult.token}`);
	}
	public get canViewMenuLinkSetup() {
		return (
			get(this.featuresList, ["NavMenu", "ExternalMenuLinks"]) &&
			(this.getPermissions.isAdmin ||
				this.getPermissions.canViewExternalMenuLinks ||
				this.getPermissions.canEditExternalMenuLinks) &&
			this.externalRoutes.length > 0
		);
	}

	private getIconImage(menuLink: ExternalMenuLink): string {
		if (!menuLink.iconFile) return;
		let identifier = menuLink.iconFile.charAt(0);
		return `data:image/${this.imageEncodeType.get(identifier)};base64,` + menuLink.iconFile;
	}

	private get getListClass(): string {
		if (this.canViewMenuLinkSetup) {
			return "route-list";
		} else return "route-list-long";
	}
}
